import apiBaseUrl from '../../utils/config.js';

class DoctorInfoService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/doctorinfo`;
  }

  getToken() {
    return localStorage.getItem('jwt');
  }

  getAuthHeaders() {
    const token = this.getToken();

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  getPublicHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  async parseResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';

    let data;
    if (contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const message =
        typeof data === 'string'
          ? data
          : data?.error || data?.message || fallbackMessage;

      throw new Error(message || fallbackMessage);
    }

    return data;
  }

  // =========================================================
  // PUBLIC ENDPOINTS
  // =========================================================

  async getPublicCareTeam() {
    const response = await fetch(`${this.baseUrl}/public/care-team`, {
      method: 'GET',
      headers: this.getPublicHeaders(),
    });

    return await this.parseResponse(response, 'Failed to fetch public care team');
  }

  async searchPublicCareTeam(query) {
    const response = await fetch(
      `${this.baseUrl}/public/care-team/search?query=${encodeURIComponent(query || '')}`,
      {
        method: 'GET',
        headers: this.getPublicHeaders(),
      }
    );

    return await this.parseResponse(response, 'Failed to search public care team');
  }

  async getPublicDoctorById(doctorId) {
    const response = await fetch(`${this.baseUrl}/public/care-team/${doctorId}`, {
      method: 'GET',
      headers: this.getPublicHeaders(),
    });

    return await this.parseResponse(response, 'Failed to fetch public doctor');
  }

  // =========================================================
  // DOCTOR REGISTRATION / LOGIN
  // =========================================================

  async registerDoctor(registerData) {
    const response = await fetch(`${this.baseUrl}/doctor/register/credentials`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(registerData),
    });

    return await this.parseResponse(response, 'Registration failed');
  }

  async loginDoctor(loginData) {
    const response = await fetch(`${this.baseUrl}/doctor/login`, {
      method: 'POST',
      headers: this.getPublicHeaders(),
      body: JSON.stringify(loginData),
    });

    const result = await this.parseResponse(response, 'Login failed');

    if (result.token) localStorage.setItem('jwt', result.token);
    if (result.username) localStorage.setItem('username', result.username);
    if (result.doctorId) localStorage.setItem('doctorId', result.doctorId);
    if (result.role) localStorage.setItem('role', result.role);

    return result;
  }

  // =========================================================
  // DOCTOR DETAILS
  // =========================================================

  async updateDoctorDetails(doctorId, username, details) {
    let queryParam = '';

    if (doctorId) {
      queryParam = `doctorId=${encodeURIComponent(doctorId)}`;
    } else if (username) {
      queryParam = `username=${encodeURIComponent(username)}`;
    } else {
      throw new Error('Either doctorId or username is required.');
    }

    const response = await fetch(`${this.baseUrl}/update/details?${queryParam}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(details),
    });

    return await this.parseResponse(response, 'Doctor details update failed');
  }

  async getDoctorById(doctorId) {
    const response = await fetch(`${this.baseUrl}/doctor/${encodeURIComponent(doctorId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return await this.parseResponse(response, 'Failed to fetch doctor');
  }

  async getDoctorByUsername(username) {
    const response = await fetch(
      `${this.baseUrl}/doctor/by-username/${encodeURIComponent(username)}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    return await this.parseResponse(response, `Doctor with username "${username}" not found`);
  }

  // =========================================================
  // ADMIN / MANAGER ENDPOINTS
  // =========================================================

  async getAllDoctors() {
    const response = await fetch(`${this.baseUrl}/doctor/all/details`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return await this.parseResponse(response, 'Failed to fetch doctors');
  }

  async getDoctorByIdForAdmin(doctorId) {
    const response = await fetch(`${this.baseUrl}/admin/${encodeURIComponent(doctorId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return await this.parseResponse(response, 'Failed to fetch doctor by ID for admin');
  }

  async updateDoctorAccount(doctorId, newUsername, newPassword) {
    const body = {};

    if (newUsername !== undefined && newUsername !== null) {
      body.username = newUsername;
    }

    if (newPassword !== undefined && newPassword !== null && newPassword !== '') {
      body.password = newPassword;
    }

    const response = await fetch(
      `${this.baseUrl}/update-account?doctorId=${encodeURIComponent(doctorId)}`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body),
      }
    );

    return await this.parseResponse(response, 'Account update failed');
  }

  async adminResetPassword(doctorId, newPassword) {
    const response = await fetch(
      `${this.baseUrl}/admin/${encodeURIComponent(doctorId)}/reset-password`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          password: newPassword,
        }),
      }
    );

    return await this.parseResponse(response, 'Admin password reset failed');
  }

  async deleteDoctor(doctorId) {
    const response = await fetch(`${this.baseUrl}/admin/${encodeURIComponent(doctorId)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return await this.parseResponse(response, 'Delete doctor failed');
  }

  // =========================================================
  // DOCTOR SELF-SERVICE
  // =========================================================

  async changePasswordSelf(oldPassword, newPassword) {
    const response = await fetch(`${this.baseUrl}/doctor/change-password`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        oldPassword,
        newPassword,
      }),
    });

    return await this.parseResponse(response, 'Password change failed');
  }
}

export default DoctorInfoService;